import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import ArrayFieldCard from './components/ArrayFieldCard';
import ExperiencesInput from './components/ExperiencesInput';

const TCSResumeForm = ({ 
  resumeData, 
  onUpdate, 
  validationErrors = {},
  getDynamicSections,
  renderDynamicSectionForm
}) => {
  const [activeSection, setActiveSection] = useState('candidate');

  const updateField = (field, value) => {
    onUpdate(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayField = (field, index, value) => {
    const current = resumeData[field] || [];
    const updated = [...current];
    updated[index] = { ...updated[index], ...value };
    onUpdate(prev => ({ ...prev, [field]: updated }));
  };

  const addArrayItem = (field, defaultItem = {}) => {
    const current = resumeData[field] || [];
    // Limit skills_matrix to 3 items max
    if (field === 'skills_matrix' && current.length >= 3) {
      return; // Don't add if already at max
    }
    onUpdate(prev => ({ ...prev, [field]: [...current, defaultItem] }));
  };

  const removeArrayItem = (field, index) => {
    const current = resumeData[field] || [];
    const updated = current.filter((_, i) => i !== index);
    onUpdate(prev => ({ ...prev, [field]: updated }));
  };

  // TCS-specific base sections
  const tcsBaseSections = [
    { id: 'candidate', label: 'Candidate Details' },
    { id: 'availability', label: 'Interview Availability' },
    { id: 'skills', label: 'Skills Matrix' },
    { id: 'comments', label: 'Supplier Comments' }
  ];

  // Additional base sections (like Professional template)
  const additionalBaseSections = [
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'projects', label: 'Projects' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'certifications', label: 'Certifications' }
  ];

  // Get dynamic sections
  const dynamicSections = getDynamicSections ? getDynamicSections() : [];
  
  // Combine all sections
  const sections = [
    ...tcsBaseSections,
    ...additionalBaseSections,
    ...dynamicSections
  ];

  // Education handlers
  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      location: "",
      start_date: "",
      end_date: "",
      cgpa: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", (resumeData.education || []).filter((_, i) => i !== index));
  };

  // Project handlers
  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), {
      title: "",
      description: "",
      link: "",
      year: "",
      technologies: []
    }]);
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("projects", updated);
  };

  const removeProject = (index) => {
    updateField("projects", (resumeData.projects || []).filter((_, i) => i !== index));
  };

  // Achievement handlers
  const addAchievement = () => {
    updateField("achievements", [...(resumeData.achievements || []), ""]);
  };

  const updateAchievement = (index, value) => {
    const updated = [...(resumeData.achievements || [])];
    updated[index] = value;
    updateField("achievements", updated);
  };

  const removeAchievement = (index) => {
    updateField("achievements", (resumeData.achievements || []).filter((_, i) => i !== index));
  };

  // Certification handlers
  const addCertification = () => {
    updateField("certifications", [...(resumeData.certifications || []), ""]);
  };

  const updateCertification = (index, value) => {
    const updated = [...(resumeData.certifications || [])];
    updated[index] = value;
    updateField("certifications", updated);
  };

  const removeCertification = (index) => {
    updateField("certifications", (resumeData.certifications || []).filter((_, i) => i !== index));
  };

  // Limit skills_matrix to 3 items
  const skillsMatrix = (resumeData?.skills_matrix || []).slice(0, 3);

  return (
    <div className="resume-form" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="form-content" style={{ marginTop: 0, paddingTop: 0 }}>
        <div className="form-header">
          <h2 className="form-title">TCS Candidate Screening</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'candidate' && (
            <div className="tab-panel">
              <h3 className="section-title">Candidate Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Name"
                  name="full_name"
                  value={resumeData?.full_name || ''}
                  onChange={(value) => updateField('full_name', value)}
                  placeholder="Tamilarasan R"
                  error={validationErrors.full_name}
                />

                <FormInput
                  label="Contact Number"
                  name="phone"
                  type="tel"
                  value={resumeData?.phone || ''}
                  onChange={(value) => updateField('phone', value)}
                  placeholder="9600535931"
                  error={validationErrors.phone}
                />

                <FormInput
                  label="Email ID"
                  name="email"
                  type="email"
                  value={resumeData?.email || ''}
                  onChange={(value) => updateField('email', value)}
                  placeholder="tamildevopscloud@gmail.com"
                  error={validationErrors.email}
                />

                <FormInput
                  label="LinkedIn Profile Link"
                  name="linkedin"
                  type="url"
                  value={resumeData?.linkedin || ''}
                  onChange={(value) => updateField('linkedin', value)}
                  placeholder="https://linkedin.com/in/..."
                />

                <FormInput
                  label="Current Location"
                  name="location"
                  value={resumeData?.location || ''}
                  onChange={(value) => updateField('location', value)}
                  placeholder="Bangalore"
                  error={validationErrors.location}
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium">Former TCS EMP/ BA</label>
                  <select
                    value={resumeData?.former_tcs_emp || 'No'}
                    onChange={(e) => updateField('former_tcs_emp', e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <FormInput
                  label="EMP ID (Former TCS)"
                  name="emp_id"
                  value={resumeData?.emp_id || ''}
                  onChange={(value) => updateField('emp_id', value)}
                  placeholder="NO"
                />
              </div>
            </div>
          )}

          {activeSection === 'availability' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Interview Availability</h3>
                <AddButton 
                  onClick={() => addArrayItem('interview_availability', { date: '', time_slot: '' })}
                >
                  Add Availability
                </AddButton>
              </div>
              {(resumeData?.interview_availability || []).map((item, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Availability ${index + 1}`}
                  index={index}
                  onRemove={() => removeArrayItem('interview_availability', index)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Date"
                      name={`avail-date-${index}`}
                      value={item.date || ''}
                      onChange={(value) => handleArrayField('interview_availability', index, { date: value })}
                      placeholder="3.11.2025"
                    />
                    <FormInput
                      label="Time Slot"
                      name={`avail-time-${index}`}
                      value={item.time_slot || ''}
                      onChange={(value) => handleArrayField('interview_availability', index, { time_slot: value })}
                      placeholder="3:00 PM"
                    />
                  </div>
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {activeSection === 'skills' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Skills Matrix (Max 3 Skills)</h3>
                {skillsMatrix.length < 3 && (
                  <AddButton 
                    onClick={() => addArrayItem('skills_matrix', { skill_name: '', years_experience: '' })}
                  >
                    Add Skill
                  </AddButton>
                )}
                {skillsMatrix.length >= 3 && (
                  <span className="text-sm text-destructive">Maximum 3 skills allowed</span>
                )}
              </div>
              {skillsMatrix.map((item, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Skill ${index + 1}`}
                  index={index}
                  onRemove={() => removeArrayItem('skills_matrix', index)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Skill Name"
                      name={`skill-name-${index}`}
                      value={item.skill_name || ''}
                      onChange={(value) => handleArrayField('skills_matrix', index, { skill_name: value })}
                      placeholder="Devops"
                    />
                    <FormInput
                      label="Years of Experience"
                      name={`skill-years-${index}`}
                      value={item.years_experience || ''}
                      onChange={(value) => handleArrayField('skills_matrix', index, { years_experience: value })}
                      placeholder="5.2 Years"
                    />
                  </div>
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {activeSection === 'comments' && (
            <div className="tab-panel">
              <h3 className="section-title">Supplier Screening Comments</h3>
              <FormTextarea
                label="Comments"
                name="supplier_comments"
                value={resumeData?.supplier_comments || ''}
                onChange={(value) => updateField('supplier_comments', value)}
                rows={6}
                placeholder="We have screened this profile. He have good Experience in Devops Engineer with 5.2 years."
              />
              <FormTextarea
                label="Note"
                name="supplier_note"
                value={resumeData?.supplier_note || ''}
                onChange={(value) => updateField('supplier_note', value)}
                rows={3}
                placeholder="NOTE: if any career gaps or education gaps. Must have to mention here with the reason. Why they have career or education gap"
              />
            </div>
          )}

          {/* Experience Section */}
          {activeSection === 'experience' && (
            <div className="tab-panel">
              <ExperiencesInput
                experiences={resumeData.relevant_experience || resumeData.experience}
                onChange={(updated) => {
                  onUpdate((prev) => ({
                    ...prev,
                    relevant_experience: updated,
                    experience: updated,
                  }));
                }}
                errors={validationErrors.relevant_experience || validationErrors.experience}
                title="Work Experience"
                addLabel="Add Experience"
                positionPlaceholder="Software Engineer"
                companyPlaceholder="Company Name"
                locationPlaceholder="City, Country"
                startDatePlaceholder="2020"
                endDatePlaceholder="2024 or Present"
                pointsPlaceholder="Description point"
                pointsMaxLength={300}
              />
            </div>
          )}

          {/* Education Section */}
          {activeSection === 'education' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Education</h3>
                <AddButton onClick={addEducation}>Add Education</AddButton>
              </div>
              {(resumeData.education || []).map((edu, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Education ${index + 1}`}
                  index={index}
                  onRemove={() => removeEducation(index)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Degree"
                      name={`edu-degree-${index}`}
                      value={edu.degree || ''}
                      onChange={(value) => updateEducation(index, 'degree', value)}
                      placeholder="Bachelor of Science"
                    />
                    <FormInput
                      label="Institution"
                      name={`edu-institution-${index}`}
                      value={edu.institution || ''}
                      onChange={(value) => updateEducation(index, 'institution', value)}
                      placeholder="University Name"
                    />
                    <FormInput
                      label="Location"
                      name={`edu-location-${index}`}
                      value={edu.location || ''}
                      onChange={(value) => updateEducation(index, 'location', value)}
                      placeholder="City, Country"
                    />
                    <FormInput
                      label="Start Date"
                      name={`edu-start-${index}`}
                      value={edu.start_date || edu.start_year || ''}
                      onChange={(value) => updateEducation(index, 'start_date', value)}
                      placeholder="2018"
                    />
                    <FormInput
                      label="End Date"
                      name={`edu-end-${index}`}
                      value={edu.end_date || edu.end_year || edu.graduation_date || ''}
                      onChange={(value) => updateEducation(index, 'end_date', value)}
                      placeholder="2022"
                    />
                    <FormInput
                      label="CGPA/GPA"
                      name={`edu-cgpa-${index}`}
                      value={edu.cgpa || edu.gpa || ''}
                      onChange={(value) => updateEducation(index, 'cgpa', value)}
                      placeholder="3.8"
                    />
                  </div>
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {/* Projects Section */}
          {activeSection === 'projects' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Projects</h3>
                <AddButton onClick={addProject}>Add Project</AddButton>
              </div>
              {(resumeData.projects || []).map((proj, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Project ${index + 1}`}
                  index={index}
                  onRemove={() => removeProject(index)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Project Title"
                      name={`proj-title-${index}`}
                      value={proj.title || proj.name || ''}
                      onChange={(value) => updateProject(index, 'title', value)}
                      placeholder="Project Name"
                    />
                    <FormInput
                      label="Year"
                      name={`proj-year-${index}`}
                      value={proj.year || ''}
                      onChange={(value) => updateProject(index, 'year', value)}
                      placeholder="2023"
                    />
                    <FormInput
                      label="Link"
                      name={`proj-link-${index}`}
                      value={proj.link || ''}
                      onChange={(value) => updateProject(index, 'link', value)}
                      placeholder="https://project-link.com"
                    />
                  </div>
                  <FormTextarea
                    label="Description"
                    name={`proj-desc-${index}`}
                    value={proj.description || ''}
                    onChange={(value) => updateProject(index, 'description', value)}
                    rows={4}
                    placeholder="Project description"
                  />
                  <FormInput
                    label="Technologies"
                    name={`proj-tech-${index}`}
                    value={Array.isArray(proj.technologies) ? proj.technologies.join(', ') : (proj.technologies || '')}
                    onChange={(value) => updateProject(index, 'technologies', value.split(',').map(t => t.trim()))}
                    placeholder="React, Node.js, MongoDB"
                  />
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {/* Achievements Section */}
          {activeSection === 'achievements' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Achievements</h3>
                <AddButton onClick={addAchievement}>Add Achievement</AddButton>
              </div>
              {(resumeData.achievements || []).map((achievement, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Achievement ${index + 1}`}
                  index={index}
                  onRemove={() => removeAchievement(index)}
                >
                  <FormInput
                    label=""
                    name={`achievement-${index}`}
                    value={achievement}
                    onChange={(value) => updateAchievement(index, value)}
                    placeholder="Achievement description"
                  />
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {/* Certifications Section */}
          {activeSection === 'certifications' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Certifications</h3>
                <AddButton onClick={addCertification}>Add Certification</AddButton>
              </div>
              {(resumeData.certifications || []).map((cert, index) => (
                <ArrayFieldCard
                  key={index}
                  title={`Certification ${index + 1}`}
                  index={index}
                  onRemove={() => removeCertification(index)}
                >
                  <FormInput
                    label=""
                    name={`cert-${index}`}
                    value={typeof cert === 'string' ? cert : cert.name || ''}
                    onChange={(value) => updateCertification(index, value)}
                    placeholder="Certification name"
                  />
                </ArrayFieldCard>
              ))}
            </div>
          )}

          {/* Dynamic Sections */}
          {renderDynamicSectionForm &&
            dynamicSections.map((section) => {
              if (activeSection === section.id) {
                return renderDynamicSectionForm(section.id);
              }
              return null;
            })}
        </div>
      </div>
    </div>
  );
};

export default TCSResumeForm;
